import { hardwareTypeFromDto } from '@cas/utils/translationHelpers';
import { CollectionModel, customerApi, QueryTag } from '../api';
import { Duration } from 'luxon';
import { FaultLevel, GlobalTimeRange } from '../slices/globalSlice';
import { calcPercentage } from '@cas/utils/numberHelpers';
import { getUrlParams, QueryParams } from './helpers';

export enum IgnitionMinimum {
  All = '0', // Don't send this to the API... just don't send the parameter
  FifteenMinutes = '1',
  OneHour = '2',
}

export interface HealthSummaryModel {
  totalAssets: number; // Not affected by timerange filter
  assetsL1FaultActive: number;
  assetsL1FaultTotal: number;
  assetsL2FaultActive: number;
  assetsL2FaultTotal: number;
  assetsConfigOutdated: number; // Not affected by timerange filter
  assetsNameMismatch: number; // Not affected by timerange filter
  assetsReportedRed: number; // Not affected by timerange filter
  assetsReportedYellow: number; // Not affected by timerange filter
}

interface AssetHealthModel {
  id: number;
  n: string;
  fc: number[]; // total faults
  fa: number[]; // faults active
  fd: number[]; // faults duration
  hw: string;
  ign: number;
  combCfgSts: boolean;
  stsNam: boolean;
  stsCfg: boolean;
  dtRep: string;
}

export interface AssetHealth extends AssetHealthModel {
  levelPercentages: number[];
  displayName: string;
  displayHw: string;
  ignDays: number;
  ignDuration: string;
}

export interface AssetHealthSummary {
  level: FaultLevel;
  assets: AssetHealth[];
}

export interface HealthSummaryQueryParams
  extends Partial<
    Pick<QueryParams, 'assetGroups' | 'deviceTypes' | 'faultCategories' | 'faults' | 'timeRange'>
  > {
  timeRange?: GlobalTimeRange;
}

export interface HealthSummaryQuery {
  query: HealthSummaryQueryParams;
}

export interface AssetHealthQueryParams
  extends Pick<QueryParams, 'faultLevel'>,
    Partial<
      Pick<
        QueryParams,
        'assetGroups' | 'deviceTypes' | 'ignitionMinimum' | 'softwareStatus' | 'timeRange'
      >
    > {
  timeRange?: GlobalTimeRange;
}

export interface AssetHealthQuery {
  query: AssetHealthQueryParams;
}

export const healthApi = customerApi.injectEndpoints({
  endpoints: build => ({
    getHealthSiteOverview: build.query<HealthSummaryModel, HealthSummaryQuery>({
      query: ({ query }) => ({
        url: 'api/health/siteoverview',
        params: getUrlParams(query),
      }),
      providesTags: (result, error, arg) =>
        result ? [QueryTag.SiteCache, { type: QueryTag.Health, id: 'SITEOVERVIEW' }] : [],
    }),
    getHealthAssets: build.query<AssetHealthSummary, AssetHealthQuery>({
      query: ({ query }) => ({
        url: 'api/health/assets',
        params: getUrlParams(query),
      }),
      transformResponse: (response: CollectionModel<AssetHealthModel>, meta, arg) => {
        const assets = response.items.map(asset => {
          const ignDuration = Duration.fromMillis(asset.ign * 1000).shiftTo(
            'days',
            'hours',
            'minutes',
            'seconds',
          );
          const subDayDuration = Duration.fromObject({
            hours: ignDuration.hours,
            minutes: ignDuration.minutes,
            seconds: Math.round(ignDuration.seconds),
          });

          return {
            ...asset,
            levelPercentages: asset.fd.map(v => calcPercentage(v, asset.ign)),
            displayName: asset.n.length > 8 ? `${asset.n.substring(0, 8)}...` : asset.n,
            displayHw: hardwareTypeFromDto(asset.hw),
            ignDays: Math.ceil(ignDuration.days),
            ignDuration: subDayDuration.toFormat('hh:mm:ss'),
          };
        });

        return {
          level: arg.query.faultLevel,
          assets,
        };
      },
      providesTags: (result, error, arg) =>
        result ? [QueryTag.SiteCache, { type: QueryTag.Health, id: 'ASSETS' }] : [],
    }),
  }),
  overrideExisting: false,
});

export const { useGetHealthSiteOverviewQuery, useGetHealthAssetsQuery } = healthApi;
